import * as React from 'react'
import { observer } from 'mobx-react'

import SubmissionViewModel from 'jobs/creation/SubmissionViewModel'
import Billing from '../../JobCreation/Submission/Billing'
import JobCreationViewModel from 'jobs/creation/JobCreationViewModel'
import { StepContainer } from 'jobs/creation/OutlineViewModel'
import Pricing from '../../JobCreation/Submission/Pricing'
import { useObservableCallback } from 'hooks/rx-hooks'
import Step from '../../JobCreation/JobCreationFormView/Step'
import styles from './JobCreationBilling.sass'
import CouponSelection from '../../JobCreation/Submission/CouponSelection'
import { useLoading } from 'hooks/loading-hooks'

interface JobCreationBillingProps {
  vm: JobCreationViewModel
}

export default observer(function JobCreationBilling({
  vm,
}: JobCreationBillingProps) {
  const submissionStep = vm.outline.stepsContainers.find(
    (stepContainer): stepContainer is SubmissionViewModel =>
      stepContainer instanceof SubmissionViewModel
  )

  return submissionStep && <BillingAndSummary vm={submissionStep} />
})

interface BillingAndSummaryProps {
  vm: SubmissionViewModel
}

const BillingAndSummary = observer(function Summary({
  vm,
}: BillingAndSummaryProps) {
  const loading = useLoading(vm.isLoading)

  const billingRelevant = vm.billing.isBillingRelevant
  const couponSelection = vm.billing.couponSelection
  const shouldCollectPaymentInfo = vm.billing.shouldCollectPaymentInfo
  const shouldRenderEmptyCouponSection =
    billingRelevant && couponSelection.canUseCoupons

  const billingSection = shouldCollectPaymentInfo ? (
    <Billing vm={vm.billing} compact />
  ) : null

  const couponSection = shouldRenderEmptyCouponSection && (
    <CouponSelection vm={couponSelection} compact />
  )

  return (
    <div
      className={styles.root}
      data-test={
        loading ? 'draft-editor-submission-loading' : 'draft-editor-submission'
      }
    >
      <StepView key={vm.step.stepId} stepContainer={vm}>
        {billingSection}
        {couponSection}
        <Pricing boxed vm={vm} compact />
      </StepView>
    </div>
  )
})

interface StepViewProps {
  stepContainer: StepContainer
  children: React.ReactNode
}

const StepView = observer(function StepView({
  stepContainer,
  children,
}: StepViewProps) {
  const step = stepContainer.step

  useObservableCallback(step.becomeActive$, step.onBecameActive, [
    step.becomeActive$,
  ])

  return (
    <Step
      title={''}
      isLast={step.position.last}
      descriptionHtml={''}
      active={step.isActive}
      becomeActive$={step.becomeActive$}
      onBecomeVisible={step.becomeVisible}
      onBecomeHidden={step.becomeHidden}
      data-test={`step-${step.stepId}`}
      compact
    >
      {children}
    </Step>
  )
})
